// src/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  apiLogin,
  apiSignup,
  apiGetMe,
  apiRequestMagicLink,
  apiConsumeMagicLink,
  apiGetMeV2,
} from "./lib/authApi";

const AuthContext = createContext(null);

// Oude key (password-based auth)
const LEGACY_TOKEN_KEY = "bondToken";
// Nieuwe key (magic link + DB)
const V2_AUTH_KEY = "bondAuthV2";

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    (async () => {
      // 1) Eerst kijken of er een v2-auth bestaat (magic link / DB)
      const storedV2 = localStorage.getItem(V2_AUTH_KEY);
      if (storedV2) {
        try {
          const parsed = JSON.parse(storedV2);
          if (parsed.token) {
            try {
              const res = await apiGetMeV2(parsed.token);
              if (res?.ok && res.user) {
                setToken(parsed.token);
                setCurrentUser(res.user);
                setInitializing(false);
                return;
              }
            } catch {
              // token ongeldig → weghalen
              localStorage.removeItem(V2_AUTH_KEY);
            }
          }
        } catch {
          localStorage.removeItem(V2_AUTH_KEY);
        }
      }

      // 2) Fallback: oude auth (password-based, in-memory users)
      const storedLegacy = localStorage.getItem(LEGACY_TOKEN_KEY);
      if (!storedLegacy) {
        setInitializing(false);
        return;
      }

      try {
        const res = await apiGetMe(storedLegacy);
        if (res?.ok && res.user) {
          setToken(storedLegacy);
          setCurrentUser(res.user);
        } else {
          localStorage.removeItem(LEGACY_TOKEN_KEY);
        }
      } catch {
        localStorage.removeItem(LEGACY_TOKEN_KEY);
      } finally {
        setInitializing(false);
      }
    })();
  }, []);

  /** Legacy login/signup (password-based) – laten we nog even staan **/

  const login = useCallback(async (email, password) => {
    const res = await apiLogin({ email, password });
    if (res?.ok && res.token && res.user) {
      setToken(res.token);
      setCurrentUser(res.user);
      localStorage.setItem(LEGACY_TOKEN_KEY, res.token);
      // Als je wilt, kun je hier ook V2_AUTH_KEY wissen:
      localStorage.removeItem(V2_AUTH_KEY);
      return res.user;
    }
    throw new Error("LOGIN_FAILED");
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const res = await apiSignup({ email, password, name });
    if (res?.ok && res.token && res.user) {
      setToken(res.token);
      setCurrentUser(res.user);
      localStorage.setItem(LEGACY_TOKEN_KEY, res.token);
      localStorage.removeItem(V2_AUTH_KEY);
      return res.user;
    }
    throw new Error("SIGNUP_FAILED");
  }, []);

  /** Nieuwe magic-link flow (V2) **/

  // 1) Magic link aanvragen
  const requestMagicLogin = useCallback(async (email, displayName) => {
    await apiRequestMagicLink({ email, displayName });
    // Als er geen error wordt gegooid, is het gelukt.
    // UI kan dan "check je mail / link is verstuurd" tonen.
  }, []);

  // 2) Magic link consumeren (bijv. op /auth/callback?token=...)
  const completeMagicLogin = useCallback(async (magicToken) => {
    const res = await apiConsumeMagicLink(magicToken);
    if (res?.ok && res.token && res.user) {
      setToken(res.token);
      setCurrentUser(res.user);

      // Nieuwe V2 storage setten
      localStorage.setItem(
        V2_AUTH_KEY,
        JSON.stringify({ token: res.token, user: res.user })
      );

      // Oude legacy token weghalen
      localStorage.removeItem(LEGACY_TOKEN_KEY);

      return res.user;
    }
    throw new Error("MAGIC_LOGIN_FAILED");
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setCurrentUser(null);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(V2_AUTH_KEY);
  }, []);

  const value = {
    currentUser,
    token,
    initializing,
    // Legacy
    login,
    signup,
    // Nieuw
    requestMagicLogin,
    completeMagicLogin,
    // Gemeenschappelijk
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
